'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Phone, CheckCircle } from 'lucide-react';

const SESSION_KEY = 'exitModalShown';

export default function ExitIntentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', company: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const hasShown = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY)) {
      hasShown.current = true;
    }

    const handleMouseLeave = (e: MouseEvent) => {
      if (hasShown.current) return;
      if (e.clientY <= 10) {
        hasShown.current = true;
        sessionStorage.setItem(SESSION_KEY, '1');
        setIsOpen(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleClose = () => setIsOpen(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          email: formData.email,
          message: 'Exit Intent — 30-Day Free Trial Request',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send');
      }

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please call us at (718) 748-8888.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-brand-red px-8 pt-8 pb-6 text-white text-center relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 hover:bg-white/20 rounded-full transition"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            Limited Time Offer
          </div>

          <h2 id="exit-modal-title" className="text-3xl font-extrabold leading-tight mb-2">
            Wait — Don't Miss Your<br />
            <span className="text-yellow-300">30-Day Free Trial</span>
          </h2>

          <p className="text-white/90 text-sm">
            Try AnswerOne's live 24/7 answering service completely free.<br />
            No credit card. No commitment. Cancel anytime.
          </p>
        </div>

        <div className="px-8 py-6">
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle className="mx-auto text-green-500 mb-3" size={52} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">You're In!</h3>
              <p className="text-gray-600 text-sm mb-4">
                A team member will reach out within 1 business day to get your free 30 days started.
              </p>
              <a
                href="tel:17187488888"
                className="inline-flex items-center gap-2 text-brand-red font-bold text-sm"
              >
                <Phone size={16} />
                Prefer to call? (718) 748-8888
              </a>
            </div>
          ) : (
            <>
              <ul className="space-y-2 mb-6">
                {[
                  'Real people answer every call — 24/7/365',
                  'HIPAA-compliant & professionally trained',
                  'Setup in minutes, no long-term contract',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className="text-green-500 mt-0.5 shrink-0" size={16} />
                    {item}
                  </li>
                ))}
              </ul>

              <form onSubmit={handleSubmit} className="space-y-3">
                {error && (
                  <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>
                )}

                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition text-sm"
                />

                <input
                  type="text"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition text-sm"
                />

                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Telephone number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition text-sm"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address (optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition text-sm"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-red text-white py-4 rounded-lg font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Claiming...' : 'Claim My Free 30-Day Trial'}
                </button>
              </form>

              <button
                onClick={handleClose}
                className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600 transition text-center"
              >
                No thanks, I don't want to try it free
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
